import React, { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { Plus, Edit2, Trash2, AlertCircle, X, Building2, Infinity } from 'lucide-react';
import { format } from 'date-fns';
import { Spinner } from '../../design-system';

interface OrgQuotaOverride {
  id: string;
  organization_id: string;
  quota_prs_per_month: number | null;
  quota_sast_scans_per_month: number | null;
  quota_deep_scans_per_month: number | null;
  max_repo_lines: number | null;
  max_applications: number | null;
  reason: string | null;
  set_by: string;
  created_at: string;
  updated_at: string;
}

interface OverrideFormState {
  organization_id: string;
  quota_prs_per_month: string;
  quota_sast_scans_per_month: string;
  quota_deep_scans_per_month: string;
  max_repo_lines: string;
  max_applications: string;
  unlimited_prs: boolean;
  unlimited_sast: boolean;
  unlimited_deep: boolean;
  unlimited_lines: boolean;
  unlimited_applications: boolean;
  reason: string;
}

const EMPTY_FORM: OverrideFormState = {
  organization_id: '',
  quota_prs_per_month: '',
  quota_sast_scans_per_month: '',
  quota_deep_scans_per_month: '',
  max_repo_lines: '',
  max_applications: '',
  unlimited_prs: false,
  unlimited_sast: false,
  unlimited_deep: false,
  unlimited_lines: false,
  unlimited_applications: false,
  reason: '',
};

const UNLIMITED_SENTINEL = -1;

/** Format a quota field for display: null = "Tier default", -1 = "Unlimited", else the number. */
const formatQuotaValue = (value: number | null): React.ReactNode => {
  if (value === null) return <span className="text-ui-text-tertiary italic">Tier default</span>;
  if (value === UNLIMITED_SENTINEL) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
        <Infinity size={14} /> Unlimited
      </span>
    );
  }
  return <span className="font-medium">{value.toLocaleString()}</span>;
};

/** Convert the form state for a single quota field to the API payload value. */
const formFieldToPayload = (valueStr: string, unlimitedChecked: boolean): number | null => {
  if (unlimitedChecked) return UNLIMITED_SENTINEL;
  const trimmed = valueStr.trim();
  if (trimmed === '') return null;
  return parseInt(trimmed, 10);
};

/** Populate form state from an existing override for editing. */
const overrideToFormState = (override: OrgQuotaOverride): OverrideFormState => ({
  organization_id: override.organization_id,
  quota_prs_per_month: override.quota_prs_per_month !== null && override.quota_prs_per_month !== UNLIMITED_SENTINEL
    ? String(override.quota_prs_per_month) : '',
  quota_sast_scans_per_month: override.quota_sast_scans_per_month !== null && override.quota_sast_scans_per_month !== UNLIMITED_SENTINEL
    ? String(override.quota_sast_scans_per_month) : '',
  quota_deep_scans_per_month: override.quota_deep_scans_per_month !== null && override.quota_deep_scans_per_month !== UNLIMITED_SENTINEL
    ? String(override.quota_deep_scans_per_month) : '',
  max_repo_lines: override.max_repo_lines !== null && override.max_repo_lines !== UNLIMITED_SENTINEL
    ? String(override.max_repo_lines) : '',
  max_applications: override.max_applications !== null && override.max_applications !== UNLIMITED_SENTINEL
    ? String(override.max_applications) : '',
  unlimited_prs: override.quota_prs_per_month === UNLIMITED_SENTINEL,
  unlimited_sast: override.quota_sast_scans_per_month === UNLIMITED_SENTINEL,
  unlimited_deep: override.quota_deep_scans_per_month === UNLIMITED_SENTINEL,
  unlimited_lines: override.max_repo_lines === UNLIMITED_SENTINEL,
  unlimited_applications: override.max_applications === UNLIMITED_SENTINEL,
  reason: override.reason || '',
});

// ---- Subcomponents ----

interface QuotaFieldProps {
  label: string;
  valueStr: string;
  onValueChange: (v: string) => void;
  unlimited: boolean;
  onUnlimitedChange: (v: boolean) => void;
  placeholder: string;
}

const QuotaField: React.FC<QuotaFieldProps> = ({
  label, valueStr, onValueChange, unlimited, onUnlimitedChange, placeholder,
}) => (
  <div>
    <label className="block text-sm font-medium text-ui-text-secondary mb-1">
      {label}
    </label>
    <div className="flex items-center gap-3">
      <input
        type="number"
        min="0"
        value={unlimited ? '' : valueStr}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={unlimited}
        className="flex-1 px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50"
        placeholder={unlimited ? 'Unlimited' : placeholder}
      />
      <label className="flex items-center gap-1.5 text-sm text-ui-text-secondary whitespace-nowrap cursor-pointer select-none">
        <input
          type="checkbox"
          checked={unlimited}
          onChange={(e) => onUnlimitedChange(e.target.checked)}
          className="rounded border-ui-border-primary"
        />
        Unlimited
      </label>
    </div>
    <p className="text-xs text-ui-text-tertiary mt-1">
      Leave empty to use tier default
    </p>
  </div>
);

// ---- Main Component ----

const OrgQuotaOverridesSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [overrides, setOverrides] = useState<OrgQuotaOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingOverride, setEditingOverride] = useState<OrgQuotaOverride | null>(null);
  const [form, setForm] = useState<OverrideFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchOverrides = useCallback(async () => {
    if (!api) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/org-quota-overrides');
      setOverrides(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load quota overrides');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  const closeModal = () => {
    setShowModal(false);
    setEditingOverride(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (override: OrgQuotaOverride) => {
    setEditingOverride(override);
    setForm(overrideToFormState(override));
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!api) return;
    const organizationId = editingOverride?.organization_id || form.organization_id.trim();
    if (!organizationId) {
      setFormError('Organization ID is required');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);

      const payload = {
        quota_prs_per_month: formFieldToPayload(form.quota_prs_per_month, form.unlimited_prs),
        quota_sast_scans_per_month: formFieldToPayload(form.quota_sast_scans_per_month, form.unlimited_sast),
        quota_deep_scans_per_month: formFieldToPayload(form.quota_deep_scans_per_month, form.unlimited_deep),
        max_repo_lines: formFieldToPayload(form.max_repo_lines, form.unlimited_lines),
        max_applications: formFieldToPayload(form.max_applications, form.unlimited_applications),
        reason: form.reason.trim() || null,
      };

      await api.put(`/admin/org-quota-overrides/${organizationId}`, payload);
      closeModal();
      await fetchOverrides();
    } catch (err: any) {
      setFormError(err.response?.detail || err.message || 'Failed to save override');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (override: OrgQuotaOverride) => {
    if (!api) return;
    if (!window.confirm(`Remove quota override for org ${override.organization_id}? This reverts to tier defaults.`)) return;

    try {
      await api.delete(`/admin/org-quota-overrides/${override.organization_id}`);
      await fetchOverrides();
    } catch (err: any) {
      window.alert(err.response?.detail || err.message || 'Failed to delete override');
    }
  };

  const updateForm = (patch: Partial<OverrideFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-ui-text-primary mb-2">
          Organization Quota Overrides
        </h2>
        <p className="text-ui-text-secondary">
          Set custom quota limits for specific organizations, independent of their subscription tier
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-black rounded-lg hover:bg-brand-hover transition-colors"
        >
          <Plus size={16} />
          Add Override
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size={32} />
        </div>
      )}

      {/* Empty state */}
      {!loading && overrides.length === 0 && (
        <div className="text-center py-12 text-ui-text-tertiary">
          No quota overrides configured
        </div>
      )}

      {/* Overrides list */}
      {!loading && overrides.length > 0 && (
        <div className="space-y-3">
          {overrides.map((override) => (
            <div
              key={override.id}
              className="bg-ui-background-secondary rounded-lg p-4 border border-ui-border-primary"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Org ID */}
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={16} className="text-ui-text-tertiary" />
                    <span className="font-mono text-sm font-medium text-ui-text-primary truncate">
                      {override.organization_id}
                    </span>
                  </div>

                  {/* Quotas grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                    <div>
                      <span className="text-ui-text-tertiary text-xs">PRs/mo</span>
                      <div>{formatQuotaValue(override.quota_prs_per_month)}</div>
                    </div>
                    <div>
                      <span className="text-ui-text-tertiary text-xs">SAST scans/mo</span>
                      <div>{formatQuotaValue(override.quota_sast_scans_per_month)}</div>
                    </div>
                    <div>
                      <span className="text-ui-text-tertiary text-xs">Deep scans/mo</span>
                      <div>{formatQuotaValue(override.quota_deep_scans_per_month)}</div>
                    </div>
                    <div>
                      <span className="text-ui-text-tertiary text-xs">Max repo lines</span>
                      <div>{formatQuotaValue(override.max_repo_lines)}</div>
                    </div>
                    <div>
                      <span className="text-ui-text-tertiary text-xs">Max repositories</span>
                      <div>{formatQuotaValue(override.max_applications)}</div>
                    </div>
                  </div>

                  {/* Meta */}
                  {override.reason && (
                    <p className="text-sm text-ui-text-secondary mb-1">
                      Reason: {override.reason}
                    </p>
                  )}
                  <div className="text-xs text-ui-text-tertiary">
                    Updated {format(new Date(override.updated_at), 'MMM d, yyyy')}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => openEditModal(override)}
                    className="p-2 text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(override)}
                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-ui-background-secondary rounded-xl p-6 w-full max-w-lg m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-ui-text-primary">
                {editingOverride ? 'Edit Quota Override' : 'Add Quota Override'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 text-ui-text-tertiary hover:text-ui-text-primary"
              >
                <X size={20} />
              </button>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              {/* Organization ID (only on create) */}
              {!editingOverride && (
                <div>
                  <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                    Organization ID *
                  </label>
                  <input
                    type="text"
                    value={form.organization_id}
                    onChange={(e) => updateForm({ organization_id: e.target.value })}
                    className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary focus:border-transparent font-mono text-sm"
                    placeholder="org_abc123..."
                    required
                  />
                </div>
              )}

              <QuotaField
                label="PRs per month"
                valueStr={form.quota_prs_per_month}
                onValueChange={(v) => updateForm({ quota_prs_per_month: v })}
                unlimited={form.unlimited_prs}
                onUnlimitedChange={(v) => updateForm({ unlimited_prs: v })}
                placeholder="e.g. 100"
              />

              <QuotaField
                label="SAST scans per month"
                valueStr={form.quota_sast_scans_per_month}
                onValueChange={(v) => updateForm({ quota_sast_scans_per_month: v })}
                unlimited={form.unlimited_sast}
                onUnlimitedChange={(v) => updateForm({ unlimited_sast: v })}
                placeholder="e.g. 50"
              />

              <QuotaField
                label="Deep scans per month"
                valueStr={form.quota_deep_scans_per_month}
                onValueChange={(v) => updateForm({ quota_deep_scans_per_month: v })}
                unlimited={form.unlimited_deep}
                onUnlimitedChange={(v) => updateForm({ unlimited_deep: v })}
                placeholder="e.g. 20"
              />

              <QuotaField
                label="Max repo lines"
                valueStr={form.max_repo_lines}
                onValueChange={(v) => updateForm({ max_repo_lines: v })}
                unlimited={form.unlimited_lines}
                onUnlimitedChange={(v) => updateForm({ unlimited_lines: v })}
                placeholder="e.g. 500000"
              />

              <QuotaField
                label="Max repositories"
                valueStr={form.max_applications}
                onValueChange={(v) => updateForm({ max_applications: v })}
                unlimited={form.unlimited_applications}
                onUnlimitedChange={(v) => updateForm({ unlimited_applications: v })}
                placeholder="e.g. 5"
              />

              <div>
                <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  value={form.reason}
                  onChange={(e) => updateForm({ reason: e.target.value })}
                  className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder="Enterprise customer, beta partner, etc."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-ui-border-primary rounded-lg text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-brand-primary text-black rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
                disabled={submitting || (!editingOverride && !form.organization_id.trim())}
              >
                {submitting ? 'Saving...' : editingOverride ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgQuotaOverridesSection;
